import { Flex } from '@utilitywarehouse/hearth-react';
import clsx from 'clsx';
import { Formik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { DialogID, useDialog } from '@components/Dialog';
import {
  UpdatingModal,
  UpdatingModalProps,
  UpdatingModalVariant,
} from '@components/UpdatingModal';
import { FeatureFlags } from '@constants/featureFlags';
import { paths } from '@constants/paths';
import {
  AppointmentParticipantType,
  AppointmentType,
  PartnerConnector,
} from '@generated/gql/graphql';
import { useAttribution } from '@hooks/useAttribution';
import { useFeatureFlag } from '@hooks/useFeatureFlag';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { BooleanSelection } from '@src/types/index';
import { isNullish } from '@utils/isNullish';

import { PartnerForm } from './Form';
import {
  GETTING_QUOTE,
  PartnerFormValues,
  SUPPORT_REMOTE,
  SupportRemoteAppointmentResult,
  initialValues,
} from './types';
import { useRemoteStarted } from './useRemoteStarted';
import { useStartPartnerAppointmentFormTracking } from './useStartAppointmentFormTracking';
import {
  addSupportingToAppointment,
  formatConnectorsOptions,
  partnerLogin,
  resolveAppointmentAttribution,
  resolveAppointmentType,
} from './utils';
import { validate } from './validate';

interface PartnerProps {
  connectors: Nullable<PartnerConnector[]>;
}

const handleEarlyNavigation = (
  values: PartnerFormValues,
  navigate: NavigateFunction
): boolean => {
  if (values.appointmentType === SUPPORT_REMOTE) {
    navigate(paths.remoteJoin);
    return true;
  }
  if (
    values.appointmentType === GETTING_QUOTE &&
    values.restoreQuote === BooleanSelection.No
  ) {
    navigate(paths.address);
    return true;
  }
  return false;
};

const navigateToAppointment = (
  appointmentType: AppointmentType,
  navigate: NavigateFunction,
  isNewRemoteEnabled: boolean
) => {
  switch (appointmentType) {
    case AppointmentType.FaceToFace:
      navigate(paths.presentation);
      break;
    case AppointmentType.PartnerRemoteWithFaceToFaceSupport:
    case AppointmentType.PartnerRemote:
    case AppointmentType.FaceToFaceWithRemoteSupport: {
      if (isNewRemoteEnabled) {
        navigate(paths.remoteLobby);
      } else {
        navigate(paths.remoteStart);
      }
      break;
    }
  }
};

const Partner = ({ connectors }: PartnerProps) => {
  const navigate = useNavigate();
  const { handleBasketCreated: restoreBasketContext, requote } =
    useBasketContext();
  const { startAppointment, isLoading, error } = useRemoteStarted();
  const { session, endAppointment, setAwaitingRemoteSupport } =
    useAppointmentContext();
  const {
    addAttributionFromAppointment,
    addAssistantDetails,
    loading: attributionLoading,
  } = useAttribution();

  const { value: isNewRemoteEnabled } = useFeatureFlag(
    FeatureFlags.NEW_REMOTE_JOURNEY,
    false
  );

  const { trackStartPartnerAppointmentClicked } =
    useStartPartnerAppointmentFormTracking();

  const updatingDialog = useDialog(DialogID.UPDATING_BUNDLE);
  const supportLoginModal = useDialog(DialogID.SUPPORT_LOGIN);
  const [hasErrorBeenAcknowledged, setHasErrorBeenAcknowledged] =
    useState(false);

  const [supportingLoginError, setSupportingLoginError] = useState('');

  const handleCloseErrorModal = useCallback(() => {
    updatingDialog.setOpen(false);
    setHasErrorBeenAcknowledged(true);
  }, [updatingDialog]);

  const supportingPartnerLogin = async (email: string, password: string) => {
    try {
      const { data, errors } = await partnerLogin(email, password);
      if (errors && errors.length > 0) {
        throw new Error(
          'Error logging you in, please check your details and try again'
        );
      }
      return data.partnerLogin.accessToken;
    } catch (error) {
      if (error instanceof Error) {
        setSupportingLoginError(error.message);
      }
      throw error;
    }
  };

  const handleQuoteRestore = async (
    values: PartnerFormValues,
    appointmentId?: string
  ) => {
    const basketId = values.restoreQuoteSelection as string;
    await requote.call(basketId);
    if (appointmentId) {
      await addAttributionFromAppointment(appointmentId, basketId);
    } else {
      await addAssistantDetails(basketId);
    }
    restoreBasketContext(basketId, appointmentId ?? undefined);
  };

  const handleSupportingPartnerAuth = async (
    values: PartnerFormValues
  ): Promise<string | null> => {
    const supportLoginFilledIn =
      values.supportingPartnerEmail !== '' ||
      values.supportingPartnerPassword !== '';

    if (!supportLoginFilledIn) {
      supportLoginModal.setOpen(true);
      return null;
    }

    try {
      const token = await supportingPartnerLogin(
        values.supportingPartnerEmail,
        values.supportingPartnerPassword
      );
      return token ?? '';
    } catch (_) {
      return null;
    }
  };

  const handleOnSubmit = async (values: PartnerFormValues) => {
    setHasErrorBeenAcknowledged(false);
    trackStartPartnerAppointmentClicked(values);
    const isGettingQuote = values.appointmentType === GETTING_QUOTE;
    const hasOpenAppointment = session.appointmentId;

    if (isGettingQuote && hasOpenAppointment) {
      await endAppointment(
        session.userType === AppointmentParticipantType.Partner
      );
    }

    if (handleEarlyNavigation(values, navigate)) {
      return;
    }

    const isRestoringQuote =
      isGettingQuote && values.restoreQuote === BooleanSelection.Yes;

    if (isRestoringQuote) {
      await handleQuoteRestore(values);
      navigate(paths.bundle);
      return;
    }

    const requiresSupportingPartnerLogIn =
      values.hasSupportingPartner === BooleanSelection.Yes &&
      values.supportingPartnerType === AppointmentType.FaceToFace;

    const awaitingRemoteSupport =
      values.hasSupportingPartner === BooleanSelection.Yes &&
      values.supportingPartnerType === AppointmentType.PartnerRemote;

    let token: Nullable<string> = '';
    if (requiresSupportingPartnerLogIn) {
      token = await handleSupportingPartnerAuth(values);
    }

    // Error or open supported partner dialog
    if (isNullish(token)) {
      return;
    }

    const resolvedAppointmentType = resolveAppointmentType(values);
    const attribution = resolveAppointmentAttribution(values, connectors);
    const { data: createAppointmentData } = await startAppointment(
      resolvedAppointmentType,
      attribution
    );
    const appointmentId =
      createAppointmentData?.createAppointment?.appointment?.id ?? null;

    if (isNullish(appointmentId)) {
      return;
    }

    let supportedAppointmentData: Nullable<SupportRemoteAppointmentResult> =
      null;

    if (token) {
      supportedAppointmentData = await addSupportingToAppointment(token);
      if (
        supportedAppointmentData.errors &&
        supportedAppointmentData.errors.length > 0
      ) {
        setSupportingLoginError(supportedAppointmentData.errors[0].message);
        return;
      }
    }

    setAwaitingRemoteSupport(awaitingRemoteSupport);

    const isRestoringAppointmentQuote =
      values.restoreQuote === BooleanSelection.Yes &&
      values.restoreQuoteSelection;

    if (isRestoringAppointmentQuote) {
      await handleQuoteRestore(values, appointmentId);
    }
    navigateToAppointment(
      resolvedAppointmentType,
      navigate,
      isNewRemoteEnabled
    );
  };

  const isUpdating = isLoading || requote.loading || attributionLoading;
  const hasError = Boolean(error || requote.error) && !hasErrorBeenAcknowledged;

  const getDialogProps = (): UpdatingModalProps => {
    if (isUpdating) {
      return {
        variant: UpdatingModalVariant.TEXT_ABOVE,
        heading: 'Starting appointment...',
      };
    }
    if (hasError) {
      return {
        variant: UpdatingModalVariant.ERROR,
        heading: 'Sorry! Something went wrong',
        content: 'Please try to join the appointment again.',
        onClose: handleCloseErrorModal,
      };
    }
    return { heading: '' };
  };

  useEffect(() => {
    updatingDialog.setOpen(isUpdating || hasError);
  }, [isUpdating, hasError, updatingDialog]);

  return (
    <>
      <Flex
        className={clsx('wrapper', 'partnerAppointment')}
        direction="column"
      >
        <Formik
          initialValues={initialValues}
          onSubmit={handleOnSubmit}
          validate={validate}
        >
          <PartnerForm
            supportingLoginError={supportingLoginError}
            connectorsOptions={
              connectors && formatConnectorsOptions(connectors)
            }
          />
        </Formik>
      </Flex>
      <UpdatingModal {...getDialogProps()} />
    </>
  );
};

export { Partner };
